/**
 * 連絡帳製本の期間選択用オブジェクト
 */

export interface Period {
  startMonth: number; // 開始月 (1, 4, 7, 10)
  endMonth: number; // 終了月 (3, 6, 9, 12)
  displayStartMonth: string; // 表示用開始月(数字) 例: "04"
  displayEndMonth: string; // 表示用終了月(数字) 例: "06"
  displayYearMonth: string; // 表示用年月 例: "2025 Apr-Jul"
  fiscalYear: number; // 年度
  calendarYear: number; // 西暦
  text: string; // セレクトボックス用文字 例: "2025年4月〜6月"
  disabled: boolean; // 現在時刻が対象期間の開始前なら true
}

// 月の省略英語名
const MONTH_ABBREV: { [key: number]: string } = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec"
};

// 四半期の開始月（year, start_monthの昇順）
const QUARTER_START_MONTHS = [4, 7, 10, 1];

/**
 * 月を2桁のゼロパディング文字列に変換
 */
function padMonth(month: number): string {
  return month.toString().padStart(2, "0");
}

/**
 * 開始月から終了月を計算（3ヶ月間）
 * 4 → 6, 7 → 9, 10 → 12, 1 → 3
 */
function calculateEndMonth(startMonth: number): number {
  return ((startMonth - 1 + 2) % 12) + 1;
}

/**
 * 開始月から西暦を計算
 * 1月〜3月の場合は fiscal_year + 1
 */
function calculateCalendarYear(fiscalYear: number, startMonth: number): number {
  return startMonth >= 4 ? fiscalYear : fiscalYear + 1;
}

/**
 * 現在時刻が calendarYear / endMonth より前または同じ月であれば disabled とする
 */
function isDisabled(calendarYear: number, endMonth: number): boolean {
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1; // JS は 0 始まりなので +1 する

  // selectBoxの西暦(calendarYear)が現在の西暦より未来ならdisabled
  if (calendarYear > nowYear) {
    return true;
  }
  // selectBoxの西暦(calendarYear)が現在の西暦より過去ならenabled
  if (calendarYear < nowYear) {
    return false;
  }

  // calendarYearと現在の西暦が等しい場合、selectBoxのendMonthが現在の月以上ならdisabled
  // 2025年10月〜12月の場合、現在年度が2026年1月にならないと選択できない
  return endMonth >= nowMonth;
}

/**
 * 「写真なし」を追加すべきかどうか判定
 * - fiscal_year = 2025: start_month = 4 のみ追加
 * - fiscal_year <= 2024: 全てに追加
 */
function shouldAddNoPhotoLabel(
  calendarYear: number,
  startMonth: number
): boolean {
  if (calendarYear <= 2024) {
    return true;
  }
  if (calendarYear === 2025 && startMonth <= 9) {
    return true;
  }
  return false;
}

/**
 * セレクトボックス用の表示文字列を生成
 * 例: "2025年4月〜6月" または "2025年4月〜6月（写真なし）"
 */
function generateSelectText(
  calendarYear: number,
  startMonth: number,
  endMonth: number,
  addNoPhotoLabel: boolean
): string {
  const baseText = `${calendarYear}年${startMonth}月〜${endMonth}月`;
  return addNoPhotoLabel ? `${baseText}（写真なし）` : baseText;
}

/**
 * 単一のPeriodオブジェクトを生成
 */
function createPeriod(fiscalYear: number, startMonth: number): Period {
  const endMonth = calculateEndMonth(startMonth);
  const calendarYear = calculateCalendarYear(fiscalYear, startMonth);
  const addNoPhotoLabel = shouldAddNoPhotoLabel(calendarYear, startMonth);
  const disabled = isDisabled(calendarYear, endMonth);
  const displayYearMonth = `${calendarYear} ${MONTH_ABBREV[startMonth]}-${MONTH_ABBREV[endMonth]}`;

  return {
    startMonth,
    endMonth,
    displayStartMonth: padMonth(startMonth),
    displayEndMonth: padMonth(endMonth),
    displayYearMonth,
    fiscalYear,
    calendarYear,
    text: generateSelectText(
      calendarYear,
      startMonth,
      endMonth,
      addNoPhotoLabel
    ),
    disabled
  };
}

/**
 * fiscal_yearからセレクトボックス用のPeriodオブジェクト配列を生成
 * @param fiscalYear 年度（例: 2024）
 * @returns Period配列（4件、start_month = 4, 7, 10, 1 の順）
 */
function list(fiscalYear: number): Period[] {
  return QUARTER_START_MONTHS.map(startMonth =>
    createPeriod(fiscalYear, startMonth)
  );
}

const periods = {
  list
};

export default periods;
